"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getImageMimeTypeFromImageBuffer = exports.getAugmentedImageMetadata = exports.getType10ImageTypeFromType = exports.imageOverrideMetadata = exports.getMetaKeysForType = exports.sortImagesByType = exports.getRecordTypeFromBioType = exports.getRecordTypeFromModality = exports.getModalityFromBioType = exports.zipImages = exports.setImageIdc = exports.getImageMetadata = exports.decodeWsq = exports.isWsq = exports.convert = exports.useSharp = void 0;
const jfd_1 = require("@le2/jfd");
const sharp_1 = __importDefault(require("sharp"));
const byte_tools_1 = require("./byte-tools");
const ebts_1 = require("./ebts");
const errors_1 = require("./errors");
const types_1 = require("./types");
let _useSharp = undefined;
/**
 * Default density to fallback if not available in the image data in DPI
 */
const IMAGE_DEFAULT_DENSITY = 500;
/**
 * Verify whether there is support for the sharp library and cache the result to a module level var for future use
 */
function useSharp() {
    return __awaiter(this, void 0, void 0, function* () {
        // Run once
        if (_useSharp !== undefined)
            return _useSharp;
        // Dummy single-pixel white PNG
        const dummy = Buffer.from('iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQAAAAA3bvkkAAAABGdBTUEAALGPC/xhBQAAACBjSFJN\n' +
            'AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QAAd2KE6QAAAAHdElN\n' +
            'RQflBgoLDgtvpC6yAAAACklEQVQI12NoAAAAggCB3UNq9AAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAy\n' +
            'MS0wNi0xMFQxNjoxNDoxMS0wNTowMH8bm3YAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjEtMDYtMTBU\n' +
            'MTY6MTQ6MTEtMDU6MDAORiPKAAAAAElFTkSuQmCC', 'base64');
        // Load dummy image to test
        try {
            // noinspection TypeScriptValidateJSTypes
            yield sharp_1.default(Buffer.from(dummy)).metadata();
            _useSharp = true;
        }
        catch (e) {
            console.warn('Sharp library support is disabled.');
            _useSharp = false;
        }
        return _useSharp;
    });
}
exports.useSharp = useSharp;
// Load dummy image to verify sharp support
/**
 * Receive an image and convert it to the specified format, or pass-through the original buffer
 * @param image A byte-array that represents an image
 * @param format The desired output format
 * @param compression The rate of compression/quality (depends on the chosen format)
 */
function convert(image, format, compression) {
    return __awaiter(this, void 0, void 0, function* () {
        // Initialize empty buffer
        let buffer = null;
        // Sharp-mode branch
        if (yield useSharp()) {
            const sharpObj = yield sharp_1.default(Buffer.from(image));
            // Format specific calls
            switch (format) {
                case types_1.EbtsImageFormat.JPEGB:
                    buffer = sharpObj.jpeg({ quality: compression || types_1.JPEG_DEFAULT_QUALITY }).toBuffer();
                    break;
                default:
                    // Just print a warning
                    console.warn(`Unsupported format: ImageFormat.${types_1.EbtsImageFormat[format]}`);
            }
        }
        // Pass-through the original image if buffer is empty
        return buffer || image;
    });
}
exports.convert = convert;
/**
 * Detects whether the buffer corresponds to a WSQ image
 */
function isWsq(buffer) {
    // First 2 bytes that delimit the start of the image are 0xff, 0xa0
    return buffer[0] === 0xff && buffer[1] === 0xa0;
}
exports.isWsq = isWsq;
/**
 * Decode a WSQ buffer to extract its fields
 * @param image
 */
function decodeWsq(image) {
    // Decode header, starting from the 7th byte up to the first 0xff
    const headerStart = image.slice(6);
    const headerEnd = headerStart.indexOf(0xff);
    const headerBinary = headerStart.slice(0, headerEnd);
    const decodedHeader = byte_tools_1.decode(headerBinary);
    // Map-reduce fields into an object
    return decodedHeader
        .split('\n')
        .map((field) => field.split(' '))
        .reduce((wsqFields, [field, value]) => {
        return Object.assign(Object.assign({}, wsqFields), { [field]: value });
    }, {});
}
exports.decodeWsq = decodeWsq;
/**
 * Get metadata from an image buffer
 * @param imageBytesArray
 */
function getImageMetadata(imageBytesArray) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        // Don't even try to use Sharp if it's a WSQ
        if (isWsq(imageBytesArray)) {
            const fields = decodeWsq(imageBytesArray);
            // Gather meta
            const width = parseInt(fields['PIX_WIDTH']);
            const height = parseInt(fields['PIX_HEIGHT']);
            const resolution = parseInt(fields['PPI']);
            let compression;
            // Determine compression algorithm value
            switch (fields['COMPRESSION']) {
                case 'WSQ':
                    compression = types_1.EbtsImageFormat.WSQ20;
                    break;
                default:
                    throw new Error('Could not determine WSQ compression algorithm');
            }
            // Validate minimum mandatory fields
            if ([resolution, width, height].some((v) => !v))
                throw new Error(`Could not decode some necessary fields: ${JSON.stringify({ resolution, width, height })}`);
            // Return basics of a WSQ file. We provided numerical resolution and so set scale to PPI.
            return {
                resolution,
                width,
                height,
                scale: types_1.EbtsScaleUnits.PPI,
                compression
            };
        }
        // Check if Sharp is available
        if (yield useSharp()) {
            let meta;
            try {
                meta = yield sharp_1.default(Buffer.from(imageBytesArray)).metadata();
            }
            catch (e) {
                throw new errors_1.UnsupportedImageFormatError(imageBytesArray, e.message);
            }
            // Detect format
            let compression;
            switch (meta.format) {
                case 'jpeg':
                    compression = types_1.EbtsImageFormat.JPEGB;
                    break;
                case 'png':
                    compression = types_1.EbtsImageFormat.PNG;
                    break;
                default:
                    throw new errors_1.InvalidImageFormatError(meta.format || 'n/a');
            }
            // Detect color space
            let colorSpace;
            switch (meta.space) {
                case 'srgb':
                    colorSpace = types_1.EbtsColorSpace.RGB;
                    break;
                case 'bw':
                case 'b-w':
                    colorSpace = types_1.EbtsColorSpace.GRAY;
                    break;
                default:
                    throw new Error('Could not determine color space of the image');
            }
            // It's safe to fallback to a default resolution
            const resolution = (_a = meta.density) !== null && _a !== void 0 ? _a : IMAGE_DEFAULT_DENSITY;
            const width = meta.width;
            const height = meta.height;
            // Validate mandatory fields
            if ([width, height].some((v) => !v))
                throw new Error(`Could not determine some necessary fields: ${JSON.stringify({ width, height })}`);
            // Return image metadata. We do not provide numerical resolution and so set scale to None.
            return {
                resolution: 0,
                width,
                height,
                compression,
                scale: types_1.EbtsScaleUnits.None,
                colorSpace
            };
        }
        // Fallback to returning some dummy meta
        return {
            resolution: 0,
            width: 0,
            height: 0,
            compression: types_1.EbtsImageFormat.NONE,
            scale: types_1.EbtsScaleUnits.None,
            colorSpace: types_1.EbtsColorSpace.RGB
        };
    });
}
exports.getImageMetadata = getImageMetadata;
/**
 * Set this image's IDC
 * @param image
 * @param idc
 */
function setImageIdc(image, idc) {
    return Object.assign(Object.assign({}, image), { idc: idc });
}
exports.setImageIdc = setImageIdc;
/**
 * Zip together images from the JFD and raw data under the corresponding EBTS Record Type key
 * @param images
 * @param data
 * @return A typed-record of zipped images
 */
function zipImages(images, data) {
    // Return if nothing can be mapped
    if (!images || !data)
        throw new errors_1.MismatchedImagesError();
    // Fail if lists are not of the same length
    if (images.length !== data.length)
        throw new errors_1.MismatchedImagesError();
    // Use a set to verify that the image wasn't passed twice
    const imageIds = new Set();
    // Map to a list of zipped ImageRecords
    return images.map((image) => {
        // Put data and image info together, only if image data actually exists
        // Validate that there's only one of each image
        if (imageIds.has(image.imageId))
            throw new errors_1.MismatchedImagesError();
        // Validate that raw images are not repeated as well
        const imagesById = data.filter(({ imageId }) => imageId === image.imageId);
        if (imagesById.length !== 1)
            throw new errors_1.MismatchedImagesError();
        // Get the only item from the filter
        const [rawImage] = imagesById;
        // Add to set
        imageIds.add(image.imageId);
        // Return info and data
        return {
            imageId: image.imageId,
            info: image,
            data: rawImage,
            // Set IDC to any dummy value, we'll overwrite it later
            idc: 0
        };
    });
}
exports.zipImages = zipImages;
/**
 * Get the Modality of the given BioType (if it exists)
 * @param bioType
 */
function getModalityFromBioType(bioType) {
    var _a;
    // Flatten Record of Modalities and Bio Types
    const modalityBioTypeMap = Object.entries(jfd_1.ModalityBioTypeMap);
    // Get the modality from the biotype
    const [modality] = (_a = modalityBioTypeMap.find(([_, bioTypes]) => {
        return bioTypes.indexOf(bioType) !== -1;
    })) !== null && _a !== void 0 ? _a : [];
    // Validate that a record type-biotype mapping was found for this image's bioType
    if (!modality)
        throw new errors_1.AssertionError(`No Modality corresponds to the given BioType: ${bioType}`);
    return modality;
}
exports.getModalityFromBioType = getModalityFromBioType;
function getRecordTypeFromModality(modality, jfd) {
    // If a modality was found, use that to get info from the JFD's modalities
    try {
        const modalityInfo = jfd.getModalityDef(modality);
        // If a rule for the specified modality was found, look for type info
        const recordType = modalityInfo.def.type;
        // If a recordType definition was found, skip the biotype mapping lookup
        if (recordType) {
            // Coerce into a string into an EbtsRecordType
            return `${recordType}`;
        }
    }
    catch (e) {
        if (e instanceof jfd_1.ModalityDefNotFoundError) {
            // Re-throw as an assertion error
            throw new errors_1.AssertionError(e.message);
        }
    }
    throw new errors_1.AssertionError(`Record Type information not found in the JFD for modality: ${modality}`);
}
exports.getRecordTypeFromModality = getRecordTypeFromModality;
/**
 * Get the RecordType that corresponds to the given BioType
 * @param bioType
 */
function getRecordTypeFromBioType(bioType) {
    var _a;
    // Flatten Record of Types and Bio Types
    const recordTypeBioTypeMap = Object.entries(types_1.RecordTypeBioTypeMap);
    // Find the biotype of the record
    const [recordType] = (_a = recordTypeBioTypeMap.find(([, bioTypes]) => {
        return bioTypes.indexOf(bioType) !== -1;
    })) !== null && _a !== void 0 ? _a : [];
    // Validate that a record type-biotype mapping was found for this image's bioType
    if (!recordType)
        throw new errors_1.UnsupportedBioTypeError(bioType);
    return recordType;
}
exports.getRecordTypeFromBioType = getRecordTypeFromBioType;
/**
 * Sort zipped images under a Record Type key
 */
function sortImagesByType(imageRecords, jfd) {
    return imageRecords.reduce((imagesMap, imageRecord) => {
        // Get the bioType from the imageRecord
        const bioType = imageRecord.info.bioType;
        // Get the record type from the biotype using the JFDs modality info or the record type-biotype mapping
        let recordType;
        try {
            const modality = getModalityFromBioType(bioType);
            recordType = getRecordTypeFromModality(modality, jfd);
        }
        catch (e) {
            if (e instanceof errors_1.AssertionError) {
                // Just issue a warning
                console.warn(e.message);
            }
            else {
                // Re-throw the error if it was something else
                throw e;
            }
            // Get the record type directly from the BioType as a last resort
            recordType = getRecordTypeFromBioType(bioType);
        }
        return Object.assign(Object.assign({}, imagesMap), { [recordType]: [...(imagesMap[recordType] || []), imageRecord] });
    }, {});
}
exports.sortImagesByType = sortImagesByType;
/**
 * Get the metadata keys for type
 * @param type
 */
function getMetaKeysForType(type) {
    // Construct the horizontal and vertical line length field keys
    const typeSuffix = `T${type}`;
    // Construct an initial default key mapping
    const metaKeys = {
        agency: `SourceAgencyORI${typeSuffix}`,
        hll: `HorizontalLineLength${typeSuffix}`,
        vll: `VerticalLineLength${typeSuffix}`,
        scale: `ScaleUnits${typeSuffix}`,
        hps: `HorizontalPixelScale${typeSuffix}`,
        vps: `VerticalPixelScale${typeSuffix}`,
        compression: `CompressionAlgorithm${typeSuffix}`,
        color: `ColorSpace${typeSuffix}`
    };
    // Generate a mapping of changes
    const metaKeysMapping = {
        [types_1.EbtsRecordType.Type10]: {
            agency: 'SourceAgencyORI',
            scale: 'ScaleUnits',
            hps: 'HorizontalPixelScale',
            vps: 'VerticalPixelScale',
            compression: 'CompressionAlgorithm',
            color: 'ColorSpace'
        }
    };
    // Merge defaults and the key changes
    return Object.assign(Object.assign({}, metaKeys), (metaKeysMapping[type] || {}));
}
exports.getMetaKeysForType = getMetaKeysForType;
/**
 * Override an ImageRecord's metadata, according to its RecordType
 * @param image
 * @param type
 * @param sourceAgency
 */
function imageOverrideMetadata(image, type, sourceAgency) {
    return __awaiter(this, void 0, void 0, function* () {
        const meta = image.info.metadata;
        // Check that image has a metadata field
        if (!meta)
            throw new errors_1.ImageHasNoMetadata();
        // Derive metadata from the image buffer
        const derivedMeta = yield getImageMetadata(image.data.data);
        let metadata;
        switch (type) {
            // Process Type-4 fingerprints
            case types_1.EbtsRecordType.Type4:
                const [fingerprintPosition, impressionType] = ebts_1.getFingerprintDetailsFromBioType(image.info.bioType);
                metadata = Object.assign(Object.assign({}, meta), { 
                    // 18 is the length of the header bytes
                    length: `${image.data.data.length + 18}`, impressionType: `${impressionType}`, fingerPositions: `${fingerprintPosition}`, imageScanningResolution: `${derivedMeta.resolution}`, horizontalLineLength: `${derivedMeta.width}`, verticalLineLength: `${derivedMeta.height}`, compressionAlgorithm: `${derivedMeta.compression}` });
                break;
            // Process SMTs, Type-14 Fingerprints and Palms
            case types_1.EbtsRecordType.Type10:
            case types_1.EbtsRecordType.Type14:
            case types_1.EbtsRecordType.Type15:
            case types_1.EbtsRecordType.Type17:
                // Get meta keys for type
                const { agency, hll, vll, scale, hps, vps, compression, color } = getMetaKeysForType(type);
                // Transform and fill in image metadata
                metadata = Object.assign(Object.assign({}, getAugmentedImageMetadata(image.info)), { [agency]: sourceAgency, [hll]: `${derivedMeta.width}`.padStart(3, '0'), [vll]: `${derivedMeta.height}`.padStart(3, '0'), [scale]: `${derivedMeta.scale}`, [hps]: `${derivedMeta.resolution}`, [vps]: `${derivedMeta.resolution}`, [compression]: types_1.EbtsImageFormat[derivedMeta.compression], [color]: types_1.EbtsColorSpace[derivedMeta.colorSpace] });
                break;
            default:
                throw new Error(`Unexpected record type: ${type}`);
        }
        return Object.assign(Object.assign({}, image), { info: Object.assign(Object.assign({}, image.info), { metadata }) });
    });
}
exports.imageOverrideMetadata = imageOverrideMetadata;
/**
 * Translate the value from the smtType metadata value into a valid ImageType value
 * @param smtType
 */
function getType10ImageTypeFromType(smtType) {
    switch (smtType) {
        case 'SCAR':
        case 'TATTOO':
            return types_1.Type10ImageType[smtType];
        default:
            return types_1.Type10ImageType.MARK;
    }
}
exports.getType10ImageTypeFromType = getType10ImageTypeFromType;
/**
 * Take in an image metadata record and produce a new record with the metadata ready for EBTS generation
 * @param image
 */
function getAugmentedImageMetadata(image) {
    // Halt if the capture date is not set
    if (!image.capturedAt) {
        throw new Error(`Image capture date can't be derived from Image.capturedAt`);
    }
    const metadata = image.metadata;
    const capturedAt = ebts_1.getEBTSDateStringFromDate(image.capturedAt, 'YYYYMMDD');
    const modality = getModalityFromBioType(image.bioType);
    if (modality === jfd_1.Modality.Face) {
        // Set the ImageType and Pose if the modality is FACE
        metadata.ImageType = types_1.Type10ImageType.FACE; // T10_IMT (10.003): FACE|SCAR|MARK|TATTOO
        metadata.PhotoDate = capturedAt; // T10_PHD (10.005)
        metadata.SubjectPose = types_1.subjectBioTypePoseMap[image.bioType]; // T10_POS (10.020)
        if (image.bioType === jfd_1.BioType.FaceFront) {
            metadata.SubjectAcquisitionProfile = '11'; // T10_SAP (10.013): 11 = 'ANSI Full Frontal facial image (ANSI 385)'
        }
        else {
            metadata.SubjectAcquisitionProfile = '0'; // T10_SAP (10.013): 0 = 'Unknown profile'
        }
        metadata.SubjectFacialDescription = 'NEUTRAL';
    }
    else if (modality === jfd_1.Modality.Palm) {
        // Set palmprint specific fields
        metadata.ImpressionTypeT15 = '10'; // T15_IMP (15.003), 10 == 'Live-scan palm'
        metadata.PalmprintCaptureDate = capturedAt; // T15_PCD (15.005)
        metadata.BitsPerPixelT15 = '8'; // T15_BPX (15.012), we can hard-code this, this isn't changing overnight
        metadata.PalmprintPosition = jfd_1.PalmPrintPositionMap[image.bioType]; // T15_PLP (15.013)
    }
    else if (modality === jfd_1.Modality.SMT) {
        if (metadata.smtType) {
            metadata.ImageType = getType10ImageTypeFromType(metadata.smtType); // T10_IMT (10.003): FACE|SCAR|MARK|TATTOO
        }
        metadata.PhotoDate = capturedAt; // T10_PHD (10.005)
    }
    else if (modality === jfd_1.Modality.Iris) {
        // TODO handle Iris metadata
        metadata.IrisCaptureDate = capturedAt; // T17_ICD (17.005)
        metadata.BitsPerPixelT17 = '8'; // T17_BPX (17.012), similar to T15_BPX (15.012), Can we just hard code this?
        metadata.FeatureIdentifier = types_1.IrisIdentifierMap[image.bioType];
    }
    return metadata;
}
exports.getAugmentedImageMetadata = getAugmentedImageMetadata;
/**
 * Get a MIME type string from an image's buffer
 * @param imageBuffer
 */
function getImageMimeTypeFromImageBuffer(imageBuffer) {
    return __awaiter(this, void 0, void 0, function* () {
        const imageMeta = yield getImageMetadata(imageBuffer);
        switch (imageMeta.compression) {
            case types_1.EbtsImageFormat.WSQ20:
                return 'application/x-wsq';
            case types_1.EbtsImageFormat.JPEGB:
            case types_1.EbtsImageFormat.JPEGL:
                return 'image/jpeg';
            case types_1.EbtsImageFormat.PNG:
                return 'image/png';
            default:
                throw new errors_1.UnsupportedImageFormatError(imageBuffer, '');
        }
    });
}
exports.getImageMimeTypeFromImageBuffer = getImageMimeTypeFromImageBuffer;
//# sourceMappingURL=images.js.map